/**
 * 수강권 일시정지 표시·판정 순수 함수.
 *
 * - 구간 판정은 서버가 준 시각끼리 비교한다: `openStartAt ≤ t < openEndAt`(끝나는 날이 없으면 `openStartAt ≤ t`).
 *   서버도 같은 규칙이다(rappo `src/utils/membershipPause.ts` — 시작일 0시 포함, 끝나는 날 0시 제외).
 * - "오늘"·"N일째"·해제 일수는 한국 시각(KST) 날짜로 낸다.
 * - 해제 미리보기는 **표시용**이다. 확정 만료일은 정지 API 응답의 만료일을 쓴다.
 */
import dayjs from 'dayjs';
import { getSmartScheduleDateKey, toSmartScheduleKst } from '@/features/smartSchedule/date';
import { MembershipPauseState, MembershipPauseSummary, PausePassKind } from './types';

export type PauseLike = MembershipPauseState | MembershipPauseSummary | null | undefined;

export type OpenPause = {
    openId: string;
    startAt: string;
    endAt: string | null;
    /** 시작일(KST 'YYYY-MM-DD') */
    startKey: string;
    /** 끝나는 날 = 해제일(KST 'YYYY-MM-DD'), 무기한이면 null */
    endKey: string | null;
};

const isValidInstant = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0 && dayjs(value).isValid();

/** 열린 정지만 꺼낸다. 열린 정지가 없거나 시작일이 이상하면 null. */
export const getOpenPause = (pause: PauseLike): OpenPause | null => {
    const openId = typeof pause?.openId === 'string' ? pause.openId.trim() : '';
    if (!openId || !isValidInstant(pause?.openStartAt)) return null;
    const endAt = isValidInstant(pause?.openEndAt) ? (pause!.openEndAt as string) : null;
    return {
        openId,
        startAt: pause!.openStartAt as string,
        endAt,
        startKey: getSmartScheduleDateKey(pause!.openStartAt),
        endKey: endAt ? getSmartScheduleDateKey(endAt) : null,
    };
};

/** 서버 판정의 "사용 중" — PT `active` / 그룹 `ACTIVE`(호환 상세는 `active`), 삭제·취소 아님. */
export const isPassInUse = (pass: { status?: unknown; isDeleted?: unknown; isCanceled?: unknown } | null | undefined) => {
    if (!pass) return false;
    const status = String(pass.status ?? '').toLowerCase();
    return status === 'active' && pass.isDeleted !== true && pass.isCanceled !== true;
};

export const getKstTodayKey = (now: Date = new Date()) => getSmartScheduleDateKey(now);

/** b − a (KST 날짜 키끼리 일수) */
export const diffDateKeys = (fromKey: string, toKey: string) =>
    dayjs(toKey).diff(dayjs(fromKey), 'day');

/** 서버 시각 → KST 'YYYY.MM.DD' (표시용) */
export const formatKstDate = (value: string | null | undefined, format = 'YYYY.MM.DD') =>
    isValidInstant(value) ? toSmartScheduleKst(value).format(format) : '-';

const formatKeyShort = (key: string) => dayjs(key).format('M/D');

export type PauseBadgeKind = 'scheduled' | 'active' | 'resuming';

export type PauseBadge = {
    kind: PauseBadgeKind;
    label: string;
    open: OpenPause;
    /** 정지 중이면 오늘이 며칠째인지(시작일 = 1일째) */
    dayCount?: number;
};

/**
 * 배지. 사용 중인 수강권에 열린 정지가 있을 때만 낸다.
 * - 오늘 < 시작일 → 정지 예정
 * - 끝나는 날 ≤ 오늘(아직 열림) → 해제 처리 중(자동 해제가 10분 주기)
 * - 그 밖 → 정지 중 (끝나는 날이 있으면 기간, 없으면 N일째)
 */
export const getPauseBadge = (
    pass: { status?: unknown; isDeleted?: unknown; isCanceled?: unknown; pause?: PauseLike } | null | undefined,
    todayKey: string = getKstTodayKey(),
): PauseBadge | null => {
    if (!isPassInUse(pass)) return null;
    const open = getOpenPause(pass?.pause);
    if (!open) return null;
    if (todayKey < open.startKey) {
        return { kind: 'scheduled', open, label: `정지 예정 · ${formatKeyShort(open.startKey)}부터` };
    }
    if (open.endKey && open.endKey <= todayKey) {
        return { kind: 'resuming', open, label: '해제 처리 중' };
    }
    const dayCount = diffDateKeys(open.startKey, todayKey) + 1;
    if (open.endKey) {
        return {
            kind: 'active',
            open,
            dayCount,
            label: `정지 중 · ${formatKeyShort(open.startKey)} ~ ${formatKeyShort(open.endKey)} 해제 예정`,
        };
    }
    return { kind: 'active', open, dayCount, label: `정지 중 · 해제일 없음 · ${dayCount}일째` };
};

/** 그 시각이 정지 구간 안인가 (서버 `isPausedAt` 의 날짜 부분과 같은 규칙) */
export const isInPauseWindow = (pause: PauseLike, instant: string | Date | null | undefined) => {
    const open = getOpenPause(pause);
    if (!open || !instant) return false;
    const t = dayjs(instant);
    if (!t.isValid()) return false;
    if (t.valueOf() < dayjs(open.startAt).valueOf()) return false;
    if (open.endAt && t.valueOf() >= dayjs(open.endAt).valueOf()) return false;
    return true;
};

const addKstDays = (value: string, days: number) => toSmartScheduleKst(value).add(days, 'day').toISOString();

export type ResumePreview = {
    /** 시작 전이라 서버가 취소로 처리(연장 0) */
    isCancel: boolean;
    /** 해제일(KST 키) */
    resumeKey: string;
    days: number;
    expiryBefore: string | null;
    /** 표시용 예상 만료일. 만료일이 없으면 null */
    expiryAfter: string | null;
};

/**
 * 해제 미리보기 — 서버 식(설계서 4-3)을 옮긴 표시용:
 * 해제일 = min(오늘, 끝나는 날), 일수 = 해제일 − 시작일, 0 이하면 취소(연장 0).
 */
export const previewResume = (
    pause: PauseLike,
    expiry: string | null | undefined,
    todayKey: string = getKstTodayKey(),
): ResumePreview | null => {
    const open = getOpenPause(pause);
    if (!open) return null;
    const resumeKey = open.endKey && open.endKey < todayKey ? open.endKey : todayKey;
    const rawDays = diffDateKeys(open.startKey, resumeKey);
    const expiryBefore = isValidInstant(expiry) ? (expiry as string) : null;
    if (rawDays <= 0) {
        return { isCancel: true, resumeKey, days: 0, expiryBefore, expiryAfter: expiryBefore };
    }
    return {
        isCancel: false,
        resumeKey,
        days: rawDays,
        expiryBefore,
        expiryAfter: expiryBefore ? addKstDays(expiryBefore, rawDays) : null,
    };
};

/**
 * 끝나는 날 변경 미리보기. 새 끝나는 날이 오늘 이하이면 서버는 그 날짜로 바로 해제한다(설계서 4-3).
 * 그 밖이면 정지가 이어지므로 만료일 변화 없음(null).
 */
export const previewChangeEnd = (
    pause: PauseLike,
    newEndKey: string | null,
    expiry: string | null | undefined,
    todayKey: string = getKstTodayKey(),
): ResumePreview | null => {
    const open = getOpenPause(pause);
    if (!open || !newEndKey || newEndKey > todayKey) return null;
    const days = Math.max(0, diffDateKeys(open.startKey, newEndKey));
    const expiryBefore = isValidInstant(expiry) ? (expiry as string) : null;
    return {
        isCancel: days === 0,
        resumeKey: newEndKey,
        days,
        expiryBefore,
        expiryAfter: expiryBefore && days > 0 ? addKstDays(expiryBefore, days) : expiryBefore,
    };
};

type ScheduleLike = {
    startTime?: string | null;
    isCanceled?: boolean;
    isDeleted?: boolean;
    bookingStatus?: string;
};

/** 그룹에서 "수업이 있었다"로 세지 않는 예약 상태(사용자 결정 2026-10-21: 대기 제외) */
const GROUP_NOT_A_LESSON = new Set(['CANCELLED', 'WAITLISTED']);

/**
 * 소급 경고용 수업 수 — 시작일 ~ 오늘 전날(KST)에 시작한 수업.
 * PT 는 상세 `schedules` 의 취소·삭제 제외, 그룹은 호환 상세 `schedules` 의 예약 상태로 거른다
 * (그룹 일정은 취소 예약도 `isCanceled:false` 로 내려온다).
 */
export const countRetroSchedules = (
    kind: PausePassKind,
    schedules: ScheduleLike[] | null | undefined,
    startKey: string,
    todayKey: string = getKstTodayKey(),
) => {
    if (!startKey || startKey >= todayKey || !Array.isArray(schedules)) return 0;
    const from = toSmartScheduleKst(startKey).valueOf();
    const to = toSmartScheduleKst(todayKey).valueOf();
    return schedules.filter((s) => {
        if (!s || !isValidInstant(s.startTime)) return false;
        if (kind === 'group') {
            if (GROUP_NOT_A_LESSON.has(String(s.bookingStatus ?? '').toUpperCase())) return false;
        } else if (s.isCanceled === true || s.isDeleted === true) {
            return false;
        }
        const t = dayjs(s.startTime).valueOf();
        return t >= from && t < to;
    }).length;
};

/** 정지 폼을 연 순간의 대상. 등록 폼은 pauseId=null. */
export type PauseFormSnapshot = { pauseId: string | null; revision: number };

/**
 * 열린 폼이 아직 같은 대상을 보고 있는가. 다시 불러온 뒤 열린 정지나 revision 이 바뀌었으면
 * 폼의 날짜는 옛 정지 기준이라 새 정지에 그대로 보내면 안 된다(교차검증 7).
 */
export const isPauseFormStale = (snapshot: PauseFormSnapshot, pause: PauseLike) =>
    snapshot.pauseId !== (getOpenPause(pause)?.openId ?? null) ||
    snapshot.revision !== Number((pause as MembershipPauseState | null | undefined)?.revision ?? 0);

/**
 * 쓰기 응답의 열린 정지가 이번 작업의 정지와 다른가 — 이미 끝난 정지의 기록이 재생됐고 지금은 다른 정지가 열려 있다.
 * 닫힘 종류(RESUME·CANCEL·END_CHANGE·HANDOVER)를 가리지 않는다(교차검증 14). 해제일만 바꿔 같은 정지가 열린 경우는 아니다.
 */
export const hasOtherOpenPause = (
    operation: { pauseId?: string | null; kind?: string } | null | undefined,
    pause: { openId?: string | null } | null | undefined,
) => !!pause?.openId && pause.openId !== (operation?.pauseId ?? null);
